import { useContext, useEffect, useMemo, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import BaseTemplate from "../../components/BaseTemplate";
import PersistentGrid from "../../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../../contexts/PersistentGridContext";
import PreFilter from "../../components/PreFilter";
import { useApi } from "../../hooks/useApi";
import { NotificationContext } from "../../components/NotificationPane";
import type { NotificationContextType, ReportParam } from "../../types";
import InternalUserRoute from "../../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../../util/AgGridFunctions";
import { useColumnDefaults } from "../../hooks/useColumnDefaults";
import { slugify } from "../../util/slugify";

interface ReportRunnerProps {
  slug: string;
}

// Report procs sometimes return a cell as a raw Excel formula string, e.g.
// =HYPERLINK("https://...","Open") (see edb_report__vn02.py's "formula" column format, used for
// _DEFAULT.json columns like agreement_hyperlink) - that's meant for Excel to evaluate, not to be
// shown as literal text here. Detected by content (not by a hardcoded column name) so it works
// for any report/column that happens to contain one, not just the one that exists today.
const EXCEL_HYPERLINK_RE = /^=HYPERLINK\(\s*"([^"]*)"\s*(?:,\s*"([^"]*)"\s*)?\)$/i;

// Most params get PreFilter's default 160px input; short fixed-format codes (e.g. p_supplier_code,
// values like "SSE" - see edb_report's per-report logs) get a narrow width sized to their actual
// content instead of wasting the same 160px as a free-text field.
const NARROW_PARAM_WIDTHS: Record<string, string> = {
  "@p_supplier_code": "60px",
};

function parseExcelHyperlink(value: unknown): { url: string; label: string } | null {
  if (typeof value !== "string") return null;
  const m = value.match(EXCEL_HYPERLINK_RE);
  if (!m) return null;
  return { url: m[1], label: m[2] || m[1] };
}

function HyperlinkOrTextCellRenderer(params: ICellRendererParams) {
  const parsed = parseExcelHyperlink(params.value);
  if (!parsed) return <>{params.valueFormatted ?? params.value}</>;
  return (
    <a
      href={parsed.url}
      target="_blank"
      rel="noopener noreferrer"
      // Matches the blue/underline hyperlink styling edb_report__vn02.py applies to the same
      // "formula" columns in the Excel output (see hyperlink_font, format: "formula" in
      // _DEFAULT.json) - Chakra's global reset otherwise leaves anchors unstyled/black.
      style={{ color: "#0563C1", textDecoration: "underline" }}
    >
      {parsed.label}
    </a>
  );
}

// Standalone (see reports_list.tsx) - no dependency on the Generic CRUD engine.
function ReportRunner({ slug }: ReportRunnerProps) {
  const { getReports, getReportParams, runReport } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;

  // The URL shows a readable slug (e.g. /reports/billval_invoice_month_gaps), not the report's
  // real numeric sk - the backend (params/run endpoints) is still entirely sk-keyed, so this
  // resolves slug -> sk once via the same report list reports_list.tsx already shows, rather than
  // adding a name-based lookup endpoint just for this. reportSk stays null until resolved;
  // everything below (params fetch, Run) waits on it.
  const [reportSk, setReportSk] = useState<number | null>(null);
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [params, setParams] = useState<ReportParam[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [loadingParams, setLoadingParams] = useState(true);
  const [running, setRunning] = useState(false);
  const [rowData, setRowData] = useState<Record<string, unknown>[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

  // See the matching comment on the params effect below re: deliberately empty/primitive-only
  // deps - getReports/pushNotification aren't stable references, depending on them causes a
  // runaway fetch loop.
  useEffect(() => {
    async function resolveSlug() {
      try {
        setResolving(true);
        const reports = await getReports();
        const match = reports.find((r) => slugify(r.reportname) === slug);
        if (match) {
          setReportSk(match.sk);
        } else {
          setNotFound(true);
        }
      } catch {
        pushNotification("Failed to load report.", "error");
        setNotFound(true);
      } finally {
        setResolving(false);
      }
    }
    resolveSlug();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Deliberately NOT depending on getReportParams/pushNotification (both come from useApi(),
  // which returns new function identities on every render - depending on them here caused an
  // infinite fetch loop: effect fires -> state update -> re-render -> new function identity ->
  // effect deps changed -> fires again. Matches DocLibraryComponent.tsx's loadFiles() effect,
  // which sidesteps this the same way - depend only on the primitive (reportSk) that should
  // actually trigger a refetch. Waits for reportSk to be resolved from the slug (above) first.
  useEffect(() => {
    if (reportSk === null) return;
    async function loadParams() {
      try {
        setLoadingParams(true);
        const data = await getReportParams(reportSk!);
        setParams(data);
        setParamValues({});
      } catch {
        pushNotification("Failed to load report parameters.", "error");
      } finally {
        setLoadingParams(false);
      }
    }
    loadParams();
    setRowData([]);
    setColumnDefs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSk]);

  const handleRun = async () => {
    if (reportSk === null) return;
    try {
      setRunning(true);
      // Blank fields are omitted entirely (not sent as "") so the proc's own CREATE PROC
      // default applies - mirrors _build_proc_call() in edb_report__vn02.py.
      const filtered: Record<string, string> = {};
      for (const p of params) {
        const v = paramValues[p.name];
        if (v !== undefined && v !== "") filtered[p.name] = v;
      }
      const result = await runReport(reportSk, filtered);
      // Columns come from the response's own column metadata, not guessed from row data, so a
      // zero-row result still renders the correct grid columns.
      const cols: ColDef[] = EDB_CUSTOM_withHeaderTooltips(
        Object.keys(result.columns).map((name) => ({ field: name, headerName: name, minWidth: 120 })),
      );
      setColumnDefs(cols);
      setRowData(result.rows);
    } catch {
      pushNotification("Failed to run report.", "error");
    } finally {
      setRunning(false);
    }
  };

  const prefilterFields = useMemo(
    () => params.map((p) => ({ ...p, width: NARROW_PARAM_WIDTHS[p.name] })),
    [params],
  );

  const defaultColDef = useMemo<ColDef>(() => ({
    ...buildDefaultColDef(columnDefaults),
    sortable: true, resizable: true, filter: true, floatingFilter: true,
    enableRowGroup: true,
    cellRenderer: HyperlinkOrTextCellRenderer,
  }), [columnDefaults]);

  if (notFound) {
    return (
      <InternalUserRoute>
        <BaseTemplate>
          <BaseTemplate.Body>
            <Text color="gray.500" p={4}>Report not found.</Text>
          </BaseTemplate.Body>
        </BaseTemplate>
      </InternalUserRoute>
    );
  }

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey={`reports-${slug}`}>
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <PersistentGridResetButton />
            <PersistentGridClearFiltersButton />
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box display="flex" flexDirection="column" h="100%" gap={2}>
              <PreFilter
                fields={prefilterFields}
                values={paramValues}
                onChange={(name, value) => setParamValues((v) => ({ ...v, [name]: value }))}
                onRun={handleRun}
                running={running}
                loading={resolving || loadingParams}
                emptyText="This report has no parameters."
              />
              <Box flex="1" minH={0}>
                <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                  <PersistentGrid<Record<string, unknown>>
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowGroupPanelShow="always"
                    animateRows
                  />
                </div>
              </Box>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>
    </InternalUserRoute>
  );
}

export default ReportRunner;
